import { useEffect, useState } from 'react';

import { Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { instanceApis } from '../../../apis/instanceApis';
import { modelApis } from '../../../apis/modelApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import SettingsTabs from '../../../components/page-sections/settings/SettingsTabs';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

const ModelSettingsPage = () => {
  const navigate = useNavigate();
  const { projectId, modelId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState(null);

  const fetchModel = async () => {
    setError(false);
    try {
      const { data: modelRes } = await modelApis.getModel(projectId, modelId);
      setModel(modelRes);
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    }
  };

  useEffect(() => {
    if (!modelId) return;
    const onPageLoad = async () => {
      setLoading(true);
      await fetchModel();
      setLoading(false);
    };
    onPageLoad();
  }, []);

  const handleDelete = async () => {
    try {
      // delete instance
      await modelApis.deleteModel(projectId, modelId);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('instance', { ns: 'instance' }),
          })
        )
      );
      const projectOverview = buildPath({
        path: PRIVATE_ROUTE.PROJECT_OVERVIEW,
        params: { projectId: projectId },
      });
      setTimeout(() => navigate(projectOverview), 800);
    } catch (errors) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('server', { ns: 'gps' }),
          })
        )
      );
    }
  };

  const handleRemoveImage = async () => {
    await instanceApis.deleteInstanceImage(projectId, modelId);
    await modelApis.getModel(projectId, modelId);
  };

  const handleUpdateModel = async (data) => {
    if (data.icon) {
      const { icon, ...form } = data;
      const formData = new FormData();
      formData.append('image', icon);
      await instanceApis.updateInstanceImage(projectId, modelId, formData);
      await modelApis.updateModel(projectId, modelId, form);
    } else {
      await modelApis.updateModel(projectId, modelId, data);
    }
    await fetchModel();
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('settingsPage', {
              ns: 'project',
              text: t('model', { ns: 'model' }),
            }),
          })
        )}
      />
    );

  return (
    <Container>
      <Breadcrumbs modelName={model.name} />
      <TitlePanel title={t('settings', { ns: 'titles' })} />
      <SettingsTabs
        instance={model}
        onDeleteInstance={handleDelete}
        onRemoveImage={handleRemoveImage}
        onUpdateInstance={handleUpdateModel}
      />
    </Container>
  );
};

export default ModelSettingsPage;
